import React, { useState, useEffect } from "react";
import { Container, Form, Button, Row, Col, Modal, Spinner, Alert } from "react-bootstrap";
import heroBg from "../assets/images/HeroBanner.png";
import { useNavigate } from "react-router-dom";
import "../styles/WebinarForm.css";

const WebinarForm = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    phone_number: "",
    email: "",
    professional_status: "",
    question: "",
    selected_webinar: "",
  });

  const [errors, setErrors] = useState({});
  const [webinars, setWebinars] = useState([]);
  const [isWebinarsLoading, setIsWebinarsLoading] = useState(true);
  const [webinarsError, setWebinarsError] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

useEffect(() => {
  const fetchWebinars = async () => {
    try {
      setIsWebinarsLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BASEURL}/getAllwebinarscms`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      const result = await response.json();

      if (response.ok) {
        // Filter: Keep only webinars that are NOT expired (webinar_date >= now)
        const now = new Date();
        const activeWebinars = (result.data || []).filter(webinar => {
          if (webinar.status !== 1) return false;

          const hasDate = webinar.webinar_date?.trim();
          const hasTime = webinar.webinar_time?.trim();

          if (hasDate && hasTime) {
            const webinarDateTime = new Date(
              `${webinar.webinar_date} ${webinar.webinar_time}`
            );

            return webinarDateTime >= new Date();
          }

          if (hasDate && !hasTime) {
            const webinarDate = new Date(webinar.webinar_date);
            const today = new Date();

            webinarDate.setHours(0, 0, 0, 0);
            today.setHours(0, 0, 0, 0);

            return webinarDate >= today;
          }
          return true;
        });

        setWebinars(activeWebinars); // No .slice() — show ALL active webinars
      } else {
        throw new Error(result.message || "Failed to fetch webinars");
      }
    } catch (error) {
      console.error("Error fetching webinars:", error);
      setWebinarsError("Failed to load webinars. Please try again.");
    } finally {
      setIsWebinarsLoading(false);
    }
  };

  fetchWebinars();
}, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
    if (error) setError("");
  };

  const validateForm = () => {
    let newErrors = {};

    if (!formData.selected_webinar) newErrors.selected_webinar = "Please select a webinar";
    if (!formData.name.trim()) newErrors.name = "Name is required";
    else if (!/^[a-zA-Z\s]+$/.test(formData.name.trim()))
      newErrors.name = "Name should contain only letters and spaces";

    if (!formData.phone_number.trim()) newErrors.phone_number = "Mobile Number is required";
    else if (!/^\d{10}$/.test(formData.phone_number.replace(/\D/g, "")))
      newErrors.phone_number = "Please enter a valid 10-digit mobile number";

    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim()))
      newErrors.email = "Please enter a valid email address";

    if (!formData.professional_status)
      newErrors.professional_status = "Professional status is required";

    if (!formData.question.trim())
      newErrors.question = "Question field is required (write 'NIL' if no question)";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const dataToSend = {
        name: formData.name.trim(),
        phone_number: formData.phone_number.trim(),
        email: formData.email.trim(),
        professional_status: formData.professional_status,
        question: formData.question.trim(),
        selected_webinar: formData.selected_webinar,
      };

      const response = await fetch(`${process.env.REACT_APP_BASEURL}/create-webinarform`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(dataToSend),
      });

      const result = await response.json();

      if (response.ok) {
        setSuccess("Form submitted successfully!");
        setShowPopup(true);
        console.log("Form submitted successfully:", result);
      } else {
        throw new Error(result.message || "Failed to submit form");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setError(error.message || "Failed to submit form. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClosePopup = () => {
    setShowPopup(false);
    setSuccess("");
    setFormData({
      name: "",
      phone_number: "",
      email: "",
      professional_status: "",
      question: "",
      selected_webinar: "",
    });
    setErrors({});
  };

  return (
    <>
      {/* Hero Section */}
      <section
        className="hero-fixed"
        style={{ backgroundImage: `url(${heroBg})` }}
      >
        <div className="hero-scroll-content">
          <div className="hero-content-home poppins-regular">
            <h1 className="hero-heading">All India Occupational Therapists' Association</h1>
          </div>
        </div>
      </section>

      {/* Content */}
      <div className="scrollable-content-home">
        <div className="content-container webinar-form-content-container">
          <section className="webinar-form-section">
            <Container className="container-webinar-form">
              <h2 className="text-center webinar-form-heading mb-4">Webinar Form</h2>

              {(error || webinarsError) && (
                <Alert variant="danger" dismissible onClose={() => { setError(""); setWebinarsError(""); }}>
                  {error || webinarsError}
                </Alert>
              )}

              {success && (
                <Alert variant="success" dismissible onClose={() => setSuccess("")}>
                  {success}
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                {/* Webinar Selection */}
                <Row>
                  <Col md={6} className="mb-4">
                    <Form.Group controlId="formWebinar">
                      <Form.Label className="form-label-webinar-form">Select Webinar *</Form.Label>
                      <div style={{ position: "relative" }}>
                        <Form.Select
                          className="form-control-webinar-form"
                          name="selected_webinar"
                          value={formData.selected_webinar}
                          onChange={handleInputChange}
                          disabled={isLoading || isWebinarsLoading}
                          style={{ paddingRight: "2.5rem" }}
                        >
                          <option value="">Select a webinar</option>
                          {webinars.map((webinar) => (
                            <option key={webinar._id} value={webinar._id}>
                              {webinar.title}
                            </option>
                          ))}
                        </Form.Select>

                        {isWebinarsLoading && (
                          <div
                            style={{
                              position: "absolute",
                              right: "10px",
                              top: "60%",
                              transform: "translateY(-50%)",
                              pointerEvents: "none",
                            }}
                          >
                            <Spinner
                              animation="border"
                              size="sm"
                              style={{ width: "2rem", height: "2rem" }}
                            />
                          </div>
                        )}
                      </div>
                      {errors.selected_webinar && (
                        <div className="text-danger mt-1">{errors.selected_webinar}</div>
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={6} className="mb-4">
                    <Form.Group controlId="formName">
                      <Form.Label className="form-label-webinar-form">Name *</Form.Label>
                      <Form.Control
                        className="form-control-webinar-form"
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Enter your name"
                        disabled={isLoading}
                      />
                      {errors.name && <div className="text-danger mt-1">{errors.name}</div>}
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mb-4">
                    <Form.Group controlId="formPhoneNumber">
                      <Form.Label className="form-label-webinar-form">Phone Number *</Form.Label>
                      <Form.Control
                        className="form-control-webinar-form"
                        type="tel"
                        name="phone_number"
                        value={formData.phone_number}
                        onChange={handleInputChange}
                        placeholder="Enter your phone number"
                        disabled={isLoading}
                      />
                      {errors.phone_number && <div className="text-danger mt-1">{errors.phone_number}</div>}
                    </Form.Group>
                  </Col>

                  <Col md={6} className="mb-4">
                    <Form.Group controlId="formEmail">
                      <Form.Label className="form-label-webinar-form">Email *</Form.Label>
                      <Form.Control
                        className="form-control-webinar-form"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="Enter your email"
                        disabled={isLoading}
                      />
                      {errors.email && <div className="text-danger mt-1">{errors.email}</div>}
                    </Form.Group>
                  </Col>
                </Row>

                {/* Professional Status */}
                <Row className="mb-4">
                  <Col>
                    <Form.Group controlId="formProfessionalStatus">
                      <Form.Label className="form-label-webinar-form">Professional Status *</Form.Label>
                      <div>
                        {["Occupational Therapist", "Occupational Therapy Student", "Others"].map((status) => (
                          <Form.Check
                            key={status}
                            type="radio"
                            className="form-check-webinar-form"
                            label={status}
                            name="professional_status"
                            value={status}
                            checked={formData.professional_status === status}
                            onChange={handleInputChange}
                            disabled={isLoading}
                          />
                        ))}
                      </div>
                      {errors.professional_status && (
                        <div className="text-danger mt-1">{errors.professional_status}</div>
                      )}
                    </Form.Group>
                  </Col>
                </Row>

                {/* Question */}
                <Row className="mb-4">
                  <Col>
                    <Form.Group controlId="formQuestion">
                      <Form.Label className="form-label-webinar-form">
                        Do you have a question to be answered by experts during the webinar? (Write "NIL" if no question) *
                      </Form.Label>
                      <Form.Control
                        className="form-control-webinar-form"
                        as="textarea"
                        name="question"
                        value={formData.question}
                        onChange={handleInputChange}
                        placeholder="Enter your question or 'NIL'"
                        rows={3}
                        disabled={isLoading}
                      />
                      <Form.Text className="text-muted">
                        Your question will be forwarded to the experts for the webinar session.
                      </Form.Text>
                      {errors.question && <div className="text-danger mt-1">{errors.question}</div>}
                    </Form.Group>
                  </Col>
                </Row>

                {/* Submit */}
<div className="text-center">
  <Button
    variant="primary"
    type="submit"
    size="lg"
    className="button-webinar-form"
    disabled={isLoading || isWebinarsLoading}
  >
    {isLoading ? (
      <>
        <Spinner 
          as="span" 
          animation="border" 
          size="sm" 
          role="status" 
          aria-hidden="true"
          style={{ width: '1.5rem', height: '1.5rem' }}
        />
        <span className="ms-2">Submit</span>
      </>
    ) : (
      "Submit"
    )}
  </Button>
</div>
              </Form>
            </Container>
          </section>
        </div>
      </div>

      {/* Success Modal */}
   <Modal show={showPopup} onHide={() => {
  handleClosePopup();
  navigate("/webinar");
}} centered>
  <Modal.Header closeButton>
    <Modal.Title>Submission Successful</Modal.Title>
  </Modal.Header>
  <Modal.Body>
    Thank you for your submission! Your form has been successfully submitted. We will contact you soon regarding the webinar.
  </Modal.Body>
  <Modal.Footer>
    <Button variant="primary" onClick={() => {
      handleClosePopup();
      navigate("/webinar");
    }}>
      Close
    </Button>
  </Modal.Footer>
</Modal>
    </>
  );
};

export default WebinarForm;
